import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { ScreenId } from '@/types/game'

declare global {
  interface Window {
    __tmhNav?: {
      go: (screen: ScreenId, opts?: { levelIndex?: number }) => void
      back: () => void
      startLevel: (levelIndex: number) => void
      replaceLevel: (levelIndex: number) => void
      screen: () => ScreenId
    }
  }
}

interface NavState {
  screen: ScreenId
  levelIndex: number | null
}

interface NavigationContextValue extends NavState {
  go: (screen: ScreenId, opts?: { levelIndex?: number }) => void
  back: () => void
  canGoBack: boolean
  startLevel: (levelIndex: number) => void
  replaceLevel: (levelIndex: number) => void
}

const NavigationContext = createContext<NavigationContextValue | null>(null)

const OVERLAY_SCREENS: ScreenId[] = ['pause']

export function NavigationProvider({ initial = 'boot', children }: { initial?: ScreenId; children: ReactNode }) {
  const [stack, setStack] = useState<NavState[]>([{ screen: initial, levelIndex: null }])

  const current = stack[stack.length - 1]

  const go = useCallback((screen: ScreenId, opts?: { levelIndex?: number }) => {
    setStack((s) => {
      const prev = s[s.length - 1]
      const next: NavState = {
        screen,
        levelIndex: opts?.levelIndex ?? (OVERLAY_SCREENS.includes(screen) ? prev.levelIndex : null),
      }
      return [...s, next]
    })
  }, [])

  const startLevel = useCallback((levelIndex: number) => {
    setStack((s) => [...s, { screen: 'game', levelIndex }])
  }, [])

  const replaceLevel = useCallback((levelIndex: number) => {
    setStack((s) => [...s.slice(0, -1), { screen: 'game', levelIndex }])
  }, [])

  const back = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s))
  }, [])

  const ctx = useMemo<NavigationContextValue>(
    () => ({
      screen: current.screen,
      levelIndex: current.levelIndex,
      go,
      back,
      startLevel,
      replaceLevel,
      canGoBack: stack.length > 1,
    }),
    [current.screen, current.levelIndex, go, back, startLevel, replaceLevel, stack.length],
  )

  useEffect(() => {
    window.__tmhNav = {
      go,
      back,
      startLevel,
      replaceLevel,
      screen: () => current.screen,
    }
    return () => {
      delete window.__tmhNav
    }
  }, [go, back, startLevel, replaceLevel, current.screen])

  return <NavigationContext.Provider value={ctx}>{children}</NavigationContext.Provider>
}

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext)
  if (!ctx) throw new Error('useNavigation must be used within <NavigationProvider>')
  return ctx
}
